import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { PageEvent } from '@angular/material/paginator';
import {
  ColDef,
  ColumnMovedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
} from 'ag-grid-community';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { PaginationConfig } from '@app/core/_config/pagination.config';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { buildColDef, ColType } from '@app/shared/utils/ag-grid.col-def.utils';
import { SnackBarConfig } from 'v2/modules/shared';
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';

// INTERFACES
import { LocalStorageKeys } from '../../../shared/interfaces/localstorage.interface';
import { IPagination } from '../../../shared/interfaces/paginator.interface';

// SERVICES
import { RfqApiService } from './../../../shared/services/api/rfq/rfq-api.service';

// CONFIG

import {
  STOCK_SNAPSHOT_AG_GRID_OPTIONS,
  STOCK_SNAPSHOT_TABLE_COL_DEFS,
} from './report-stock-snapshot-ag-table.component.config';

@Component({
  selector: 'lum-report-stock-snapshot-ag-table',
  templateUrl: './report-stock-snapshot-ag-table.component.html',
  styleUrls: ['./report-stock-snapshot-ag-table.component.scss'],
})
export class ReportStockSnapshotAgTableComponent implements OnDestroy, OnChanges, OnInit {
  @Input()
  selectedDate :Date | null = new Date();

  @Input()
  selectedDateEnd :Date | null = new Date();

  @Input() includeAllData :boolean = false;

  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    per_page: 100,
    to: null,
    total: 0,
    pageIndex: null,
  };
  currentQueryParams = {
    limit: '100',
    page: '1',
    dateSnapshot: '',
    dateSnapshotEnd: '',
  };

  loading$ = new BehaviorSubject<boolean>(false);
  gridOptions :GridOptions = {
    ...STOCK_SNAPSHOT_AG_GRID_OPTIONS,
    rowDragManaged: true,
    rowModelType: 'serverSide',
  };

  defaultColDef :any = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
    filterParams: {
      filterOptions: ['equals'],
      defaultOption: 'equals',
      maxNumConditions: 1,
    },
  };

  columnDefs :ColDef[] = STOCK_SNAPSHOT_TABLE_COL_DEFS;

  private gridApi! :GridApi;
  private gridColumnApi :any;
  private onDestroy$ = new Subject();

  constructor(
    private rfqApiService :RfqApiService,
    private snackBar :MatSnackBar,
    private featureToggleService :FeatureToggleService,
  ) {}

  ngOnInit() :void {
    if (this.featureToggleService.isActive('red_DV-3389_inventory_reconciliation_report')) {
      const size :Partial<ColDef> = {
        flex: 9,
        minWidth: 9 * 20,
      }

      this.columnDefs = [
        ...this.columnDefs,
        buildColDef(ColType.CURRENCY, {
        headerName: 'Starting Inventory Value',
        field: 'startingInventoryValue',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.QUANTITY, {
        headerName: 'Starting Inventory Count',
        field: 'startingInventoryCount',
        filter: false,
         sortable: false,
        ...size,
      }),
      buildColDef(ColType.QUANTITY, {
        headerName: 'Received Count',
        field: 'receivedCount',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.CURRENCY, {
        headerName: 'Received Value',
        field: 'receivedValue',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.QUANTITY, {
        headerName: 'Sold Count',
        field: 'soldCount',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.CURRENCY, {
        headerName: 'Sold Value',
        field: 'soldValue',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.QUANTITY, {
        headerName: 'Adjustment Count',
        field: 'adjustmentCount',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.CURRENCY, {
        headerName: 'Adjustment Value',
        field: 'adjustmentValue',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.QUANTITY, {
        headerName: 'Ending Inventory Count',
        field: 'endingInventoryCount',
        filter: false,
        sortable: false,
        ...size,
      }),
      buildColDef(ColType.CURRENCY, {
        headerName: 'Ending Inventory Value',
        field: 'endingInventoryValue',
        filter: false,
        sortable: false,
        ...size,
      }),
      ]
    }
  }

  ngOnChanges(changes :SimpleChanges) :void {
    let needsRefresh = false;
    if (
      changes['selectedDate'] &&
      changes['selectedDate'].currentValue !== changes['selectedDate'].previousValue
    ) {
      this.currentQueryParams.dateSnapshot = this.selectedDateToISO;
      needsRefresh = true;
    }

    if (
      changes['selectedDateEnd'] &&
      changes['selectedDateEnd'].currentValue !== changes['selectedDateEnd'].previousValue
    ) {
      this.currentQueryParams.dateSnapshotEnd = this.selectedDateEndToISO;
      needsRefresh = true;
    }

    if (
      changes['includeAllData'] &&
      changes['includeAllData'].currentValue !==
        changes['includeAllData'].previousValue
    ) {
      needsRefresh = true;
    }

    if (needsRefresh && this.gridApi) {
      this.gridApi.setServerSideDatasource(this.#getServerSideDatasource());
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onGridReady(params :GridReadyEvent<any>) :void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    // Apply saved column state from local storage
    const columnState = this.getColumnStateFromLocalStorage();
    if (columnState) {
      this.gridColumnApi.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }

    if (this.storedPageState && Object.keys(this.storedPageState)?.length) {
      this.gridApi?.setFilterModel(this.storedPageState);
    }

    // Listen for visibility changes
    this.gridApi.addEventListener('columnVisible', () =>
      this.onColumnStateChanged(),
    );
    const datasource = this.#getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  onColumnMoved(event :ColumnMovedEvent) :void {
    if (event.finished) {
      const savedState = this.gridColumnApi.getColumnState();
      localStorage.setItem(
        LocalStorageKeys.STOCK_SNAPSHOT_COLUMN_STATE,
        JSON.stringify(savedState),
      );
    }
  }

  onFilterChanged() :void {
    localStorage.setItem(
      LocalStorageKeys.STOCK_SNAPSHOT_FILTER_STATE,
      JSON.stringify(this.filterModel),
    );
  }

  toggleColumnVisibility(column :ColDef) :void {
    column.hide = !column.hide;
    this.gridApi.setColumnDefs(this.columnDefs);
    this.onColumnStateChanged(); // Save the state after toggling visibility
  }

  gotoPage(pageValue :string) :void {
    if (pageValue === '') {
      return;
    }
    const pageValueInput = Number(pageValue);
    const pageEvent = new PageEvent();
    pageEvent.pageIndex = pageValueInput - 1; // because pageIndex is 0-based
    pageEvent.pageSize = this.pagination.per_page;
    pageEvent.length = this.pagination.total;
    this.onPaginationEvent(pageEvent);
  }

  handleGoToPageInput(inputElement :HTMLInputElement) :void {
    const inputElementValue = Number(inputElement.value);
    if (inputElementValue === 0) {
      inputElement.value = '1';
    }
    if (inputElementValue > this.pagination.last_page) {
      inputElement.value = this.pagination.last_page.toString();
    }
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.currentQueryParams = {
      ...this.currentQueryParams,
      page: (pagination.pageIndex + 1).toString(),
      limit: pagination.pageSize.toString(),
    };

    this.gridApi?.setServerSideDatasource(this.#getServerSideDatasource());
  }

  private onColumnStateChanged() :void {
    const columnState = this.gridColumnApi.getColumnState();
    localStorage.setItem(
      LocalStorageKeys.STOCK_SNAPSHOT_COLUMN_STATE,
      JSON.stringify(columnState),
    );
  }

  private getColumnStateFromLocalStorage() :ColDef[] | null {
    const columnState = localStorage.getItem(
      LocalStorageKeys.STOCK_SNAPSHOT_COLUMN_STATE,
    );

    return columnState ? JSON.parse(columnState) : null;
  }

  #getServerSideDatasource = () :IServerSideDatasource => {
    return {
      getRows: (params :any) => {
        this.loading$.next(true);

        let queryParams :{ [key :string] :string | string[] } = {
          dateSnapshot: this.selectedDateToISO,
          dateSnapshotEnd: this.selectedDateEndToISO,
          ...AgGridUtils.mapQueryParams(
            { params, paginationPageSize: this.pagination.per_page },
            {
              defaultSortDirection: 'asc',
              defaultSortField: 'product_name',
            },
          ),
          ...this.currentQueryParams,
        };

        if (this.includeAllData) {
          queryParams['includeAllData'] = 'true';
        }

        // Since the API is expecting a different field name for sorting, we need to map it here
        queryParams = this.mapSortField(queryParams);

        this.rfqApiService
          .getRfqStockSnapshot(queryParams)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              const total =
                response.meta.total < this.pagination.per_page
                  ? response.meta.total
                  : this.pagination.per_page;
              params.successCallback(response.data, total);
              this.pagination = response.meta;
              this.loading$.next(false);
            },
            error: (error) => {
              params.failCallback();
              this.loading$.next(false);
              this.snackBar.open(
                error?.error?.message || 'Error getting RFQs',
                'Close',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
            },
          });
      },
    };
  };

  mapSortField(queryParams :{ [key :string] :string | string[] }) : { [key :string] :string | string[] } {
    switch (queryParams.sortField) {
      case 'product_name':
      case 'productName':
        queryParams.sortField = 'tb_rfqs.product_name';
        break;
      case 'locationName':
        queryParams.sortField = 'l.location_name';
        break;
      case 'mostRecentRunningBalance':
        queryParams.sortField = 'most_recent_running_balance';
        break;
      case 'remainingQuantityCost':
        queryParams.sortField = 'remaining_quantity_cost';
        break;
      case 'categoryName':
        queryParams.sortField = 'category_name';
        break;
      case 'subCategoryName':
        queryParams.sortField = 'sub_category_name';
        break;
    }

    return queryParams;
  }

  get filterModel() :any {
    return this.gridApi?.getFilterModel();
  }

  get currentColumnState() :any {
    return this.gridColumnApi?.getColumnState();
  }

  get sortModel() :any {
    const sortedColumn = this.currentColumnState.find(
      (col :any) => col.sort !== null,
    );

    return sortedColumn;
  }

  get storedPageState() :any {
    const pageState = localStorage.getItem(
      LocalStorageKeys.STOCK_SNAPSHOT_FILTER_STATE,
    );

    return pageState ? JSON.parse(pageState) : null;
  }

  get selectedDateToISO() :string {
    if (!this.selectedDate) {
      return '';
    }

    return new Date(this.selectedDate).toISOString();
  }

  get selectedDateEndToISO() :string {
    if (!this.selectedDateEnd) {
      return '';
    }

    return new Date(this.selectedDateEnd).toISOString();
  }
}
